import React, { useState, useEffect } from "react";
import { useFirebase } from "../../../context/FirebaseContext";
import { getAuth } from "firebase/auth";
import {
    getStoreSettings,
    updateStoreSettings
} from "../../../firebase/settings-functions";
import { DEFAULT_STORE_ID, uploadFile, deleteFile } from "../../../firebase/firebase";
import { useLanguage } from "../../../context/LanguageContext";
import { useAdminSettingsTranslation } from "./translations";
import "./AdminSettingsPage.css";

// Import component sections
import GeneralTab from "./tabs/GeneralTab";
import ShippingTab from "./tabs/ShippingTab";
import PaymentTab from "./tabs/PaymentTab";
import PromoCodesTab from "./tabs/PromoCodesTab";
import AnnouncementBannersTab from "./tabs/AnnouncementBannersTab";
import TaxTab from "./tabs/TaxTab";
import CountriesTab from "./tabs/CountriesTab";
import SocialMediaTab from "./tabs/SocialMediaTab";
import HomePageTab from "./tabs/HomePageTab";
import EmailConfigTab from "./tabs/EmailConfigTab";


const AdminSettings = () => {
    const { language } = useLanguage();
    const { t } = useAdminSettingsTranslation(language);

    const [storeData, setStoreData] = useState({
        multilingualContent: {
            en: {
                name: "",
                description: ""
            },
            ar: {
                name: "",
                description: ""
            }
        },
        email: "",
        phone: "",
        address: "",
        domain: "", // Custom domain for this store
        currency: "AED",
        logo: null,
        logoUrl: "",
        socialMedia: {
            facebook: "",
            instagram: "",
            twitter: ""
        },
        brandColors: {
            primary: "#182957",
            hover: "#233a79",
            text: "#181317",
            light: "#e7edf7"
        },
        shippingOptions: [],
        paymentMethods: {
            creditCard: true,
            paypal: false,
            applePay: false,
            cashOnDelivery: true,
            manualTransfer: false
        },
        bankTransferDetails: {
            en: "",
            ar: ""
        },
        availableCountries: [],
        taxRate: 0,
        taxIncluded: false,
        minimumSpendForFreeDelivery: null,
        requiresShipping: true, // Default to true for backward compatibility
        promoCodesEnabled: false
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [activeTab, setActiveTab] = useState("general");
    const [logoPreview, setLogoPreview] = useState("");
    const [debugInfo, setDebugInfo] = useState({});
    const [contextReady, setContextReady] = useState(false);


    const { authInitialized, isAuthenticatedAdmin } = useFirebase();

    // Wait for context to be ready
    useEffect(() => {


        if (authInitialized) {
            if (isAuthenticatedAdmin()) {
                // console.log('AdminSettings - Context ready, user is authenticated admin');
                setContextReady(true);
            } else {
                // console.log('AdminSettings - Context ready, but user is not authenticated admin');
                setContextReady(false);
                setError(t('mustBeLoggedInAsStoreOwner'));
            }
        }
    }, [authInitialized, isAuthenticatedAdmin]);

    useEffect(() => {
        if (!contextReady) {
            // console.log('AdminSettings - Context not ready');
            return;
        }

        const fetchStoreData = async () => {
            // console.log('AdminSettings - Starting to fetch store data');
            setLoading(true);
            setError("");



            try {
                // console.log("📊 AdminSettings - Fetching store data for:", DEFAULT_STORE_ID);
                const store = await getStoreSettings(DEFAULT_STORE_ID, { fullMultilingual: true });
                // console.log("✅ AdminSettings - Store data fetched:", store);

                // Populate the form with store data
                setStoreData({
                    multilingualContent: store.multilingualContent || {
                        en: {
                            name: store.name || "",
                            description: store.description || ""
                        },
                        ar: {
                            name: "",
                            description: ""
                        }
                    },
                    email: store.email || "",
                    phone: store.phone || "",
                    address: store.address || "",
                    domain: store.domain || "", // Add domain field
                    currency: store.currency || "AED",
                    logoUrl: store.logoUrl || "",
                    socialMedia: store.socialMedia || {
                        facebook: "",
                        instagram: "",
                        twitter: ""
                    },
                    brandColors: store.brandColors || {
                        primary: "#182957",
                        hover: "#233a79",
                        text: "#181317",
                        light: "#e7edf7"
                    },
                    shippingOptions: store.shippingOptions || [],
                    paymentMethods: store.paymentMethods || {
                        creditCard: true,
                        paypal: false,
                        applePay: false,
                        cashOnDelivery: true,
                        manualTransfer: false
                    },
                    bankTransferDetails: store.bankTransferDetails || {
                        en: "",
                        ar: ""
                    },
                    availableCountries: store.availableCountries || [
                        { code: "UAE", name: "United Arab Emirates" },
                        { code: "KSA", name: "Saudi Arabia" }
                    ],
                    taxRate: store.taxRate || 0,
                    taxIncluded: store.taxIncluded || false,
                    minimumSpendForFreeDelivery: store.minimumSpendForFreeDelivery || null,
                    requiresShipping: store.requiresShipping !== false, // Default to true for backward compatibility
                    promoCodesEnabled: store.promoCodesEnabled || false
                });

                if (store.logoUrl) {
                    setLogoPreview(store.logoUrl);
                }



            } catch (err) {
                console.error("❌ AdminSettings - Error fetching store data:", err);
                setError(t('failedToLoadStoreSettings', { message: err.message }));



            } finally {
                setLoading(false);
            }
        };

        fetchStoreData();
    }, [contextReady]);

    // Handle general form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Handle nested objects (socialMedia, paymentMethods, multilingualContent)
        if (name.includes(".")) {
            const [parent, child] = name.split(".");
            setStoreData({
                ...storeData,
                [parent]: {
                    ...storeData[parent],
                    [child]: type === "checkbox" ? checked : value
                }
            });
        } else if (name === "multilingualContent") {
            // Handle the multilingual content object
            setStoreData({
                ...storeData,
                multilingualContent: value
            });
        } else {
            setStoreData({
                ...storeData,
                [name]: type === "checkbox" ? checked : value
            });
        }
    };




    // Handle logo upload
    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Preview the selected image
        const reader = new FileReader();
        reader.onload = () => {
            setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);

        setStoreData({
            ...storeData,
            logo: file
        });
    };

    // Handle tax rate change
    const handleTaxRateChange = (e) => {
        const rate = parseFloat(e.target.value) / 100; // Convert from percentage to decimal
        setStoreData({
            ...storeData,
            taxRate: isNaN(rate) ? 0 : rate
        });
    };

    // Clear messages after a delay
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess("");
                setError("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    // Save general settings (excludes shipping, countries, and tax which have their own save buttons)
    const handleSaveSettings = async (e) => {
        e.preventDefault();

        // Validate that at least the English store name is provided
        const englishName = storeData.multilingualContent?.en?.name?.trim();
        if (!englishName) {
            setError(t('storeNameInEnglishRequired'));
            return;
        }

        setSaving(true);
        setError("");
        setSuccess("");

        try {
            // Create a copy of the store data to submit
            const storeDataToSubmit = { ...storeData };
            // Remove the logo file from the data to be sent to Firestore
            delete storeDataToSubmit.logo;

            // Ensure brandColors is included (validation happens server-side)
            if (storeData.brandColors) {
                storeDataToSubmit.brandColors = storeData.brandColors;
            }

            // If a new logo was selected, delete the old one and upload the new one
            if (storeData.logo) {
                // Delete the old logo from storage if it exists
                if (storeData.logoUrl) {
                    await deleteFile(storeData.logoUrl);
                }

                const logoUrl = await uploadFile(
                    DEFAULT_STORE_ID,
                    `settings/logo-${Date.now()}`,
                    storeData.logo
                );
                storeDataToSubmit.logoUrl = logoUrl;
            }

            // Validate before calling cloud functions
            if (!DEFAULT_STORE_ID) {
                throw new Error("Store ID is not configured. Please check your environment settings.");
            }

            // Update the store with multilingual support (general settings only)
            await updateStoreSettings(DEFAULT_STORE_ID, storeDataToSubmit);

            setSuccess(t('storeSettingsSavedSuccessfully'));

            // Refresh the data to reflect any updates
            const refreshedStore = await getStoreSettings(DEFAULT_STORE_ID, { fullMultilingual: true });
            setStoreData(prevData => ({
                ...prevData,
                multilingualContent: refreshedStore.multilingualContent,
                logoUrl: refreshedStore.logoUrl || prevData.logoUrl,
                brandColors: refreshedStore.brandColors || {
                    primary: "#182957",
                    hover: "#233a79",
                    text: "#181317",
                    light: "#e7edf7"
                }
            }));
        } catch (err) {
            // console.error("AdminSettings - Error saving store settings:", err);
            setError(t('failedToSaveSettings', { message: err.message }));
        } finally {
            setSaving(false);
        }
    };

    // Show loading while context is initializing
    if (!authInitialized) {
        return (
            <div className="admin-loading">
                <div className="loader"></div>
                <p>{t('initializingAuthentication')}</p>
            </div>
        );
    }

    // Show error if not authenticated as admin
    if (!isAuthenticatedAdmin()) {
        return (
            <div className="admin-error">
                <h2>{t('accessDenied')}</h2>
                <p>{t('mustBeLoggedInAsStoreOwner')}</p>
                <div style={{ marginTop: '20px' }}>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="admin-loading">
                <div className="loader"></div>
                <p>{t('loadingStoreSettings')}</p>
            </div>
        );
    }

    return (
        <div className="admin-settings">


            {/* Page Title */}
            <div className="admin-page-title">
                <h1>{t('storeSettings')}</h1>
                <p>{t('configureStorePreferences')}</p>
            </div>

            {error && <div className="admin-error-message">{error}</div>}
            {success && <div className="admin-success-message">{success}</div>}

            {/* Settings Tabs - THIS WAS MISSING! */}
            <div className="settings-tabs">
                <button
                    className={`settings-tab ${activeTab === "general" ? "active" : ""}`}
                    onClick={() => setActiveTab("general")}
                >
                    {t('general')}
                </button>
                <button
                    className={`settings-tab ${activeTab === "homepage" ? "active" : ""}`}
                    onClick={() => setActiveTab("homepage")}
                >
                    {t('homePage')}
                </button>
                <button
                    className={`settings-tab ${activeTab === "shipping" ? "active" : ""}`}
                    onClick={() => setActiveTab("shipping")}
                >
                    {t('shipping')}
                </button>
                <button
                    className={`settings-tab ${activeTab === "payment" ? "active" : ""}`}
                    onClick={() => setActiveTab("payment")}
                >
                    {t('payment')}
                </button>
                <button
                    className={`settings-tab ${activeTab === "promocodes" ? "active" : ""}`}
                    onClick={() => setActiveTab("promocodes")}
                >
                    {t('promoCodes')}
                </button>
                <button
                    className={`settings-tab ${activeTab === "announcements" ? "active" : ""}`}
                    onClick={() => setActiveTab("announcements")}
                >
                    {t('announcements')}
                </button>
                <button
                    className={`settings-tab ${activeTab === "tax" ? "active" : ""}`}
                    onClick={() => setActiveTab("tax")}
                >
                    {t('tax')}
                </button>
                <button
                    className={`settings-tab ${activeTab === "countries" ? "active" : ""}`}
                    onClick={() => setActiveTab("countries")}
                >
                    {t('countries')}
                </button>
                <button
                    className={`settings-tab ${activeTab === "social" ? "active" : ""}`}
                    onClick={() => setActiveTab("social")}
                >
                    {t('socialMedia')}
                </button>
                <button
                    className={`settings-tab ${activeTab === "emailconfig" ? "active" : ""}`}
                    onClick={() => setActiveTab("emailconfig")}
                >
                    {t('emailConfig')}
                </button>
            </div>

            {/* Tabs that DON'T need the main form - they have their own save buttons */}
            {(activeTab === "promocodes" || activeTab === "announcements" || activeTab === "homepage" || activeTab === "emailconfig" || activeTab === "payment" || activeTab === "shipping" || activeTab === "countries" || activeTab === "tax") && (
                <div className="settings-form">
                    {activeTab === "promocodes" && (
                        <div className="settings-section active">
                            <PromoCodesTab
                                storeId={DEFAULT_STORE_ID}
                                storeData={storeData}
                                handleChange={handleChange}
                            />
                        </div>
                    )}
                    {activeTab === "announcements" && (
                        <div className="settings-section active">
                            <AnnouncementBannersTab storeId={DEFAULT_STORE_ID} />
                        </div>
                    )}
                    {activeTab === "homepage" && (
                        <div className="settings-section active">
                            <HomePageTab storeId={DEFAULT_STORE_ID} />
                        </div>
                    )}
                    {activeTab === "emailconfig" && (
                        <div className="settings-section active">
                            <EmailConfigTab storeId={DEFAULT_STORE_ID} />
                        </div>
                    )}
                    {activeTab === "payment" && (
                        <div className="settings-section active">
                            <PaymentTab
                                storeId={DEFAULT_STORE_ID}
                                storeData={storeData}
                                setStoreData={setStoreData}
                                handleChange={handleChange}
                            />
                        </div>
                    )}
                    {activeTab === "shipping" && (
                        <div className="settings-section active">
                            <ShippingTab
                                storeData={storeData}
                                setStoreData={setStoreData}
                                setError={setError}
                                storeId={DEFAULT_STORE_ID}
                            />
                        </div>
                    )}
                    {activeTab === "countries" && (
                        <div className="settings-section active">
                            <CountriesTab
                                storeData={storeData}
                                setStoreData={setStoreData}
                                setError={setError}
                                storeId={DEFAULT_STORE_ID}
                            />
                        </div>
                    )}
                    {activeTab === "tax" && (
                        <div className="settings-section active">
                            <TaxTab
                                storeData={storeData}
                                handleChange={handleChange}
                                handleTaxRateChange={handleTaxRateChange}
                                storeId={DEFAULT_STORE_ID}
                                setError={setError}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Tabs that DO need the main form */}
            {activeTab !== "promocodes" && activeTab !== "announcements" && activeTab !== "homepage" && activeTab !== "emailconfig" && activeTab !== "payment" && activeTab !== "shipping" && activeTab !== "countries" && activeTab !== "tax" && (
                <form onSubmit={handleSaveSettings} className="settings-form">
                    {/* General Settings */}
                    {activeTab === "general" && (
                        <div className="settings-section active">
                            <GeneralTab
                                storeData={storeData}
                                handleChange={handleChange}
                                logoPreview={logoPreview}
                                handleLogoChange={handleLogoChange}
                            />
                        </div>
                    )}

                    {/* Social Media Settings */}
                    {activeTab === "social" && (
                        <div className="settings-section active">
                            <SocialMediaTab
                                storeData={storeData}
                                handleChange={handleChange}
                            />
                        </div>
                    )}

                    {/* Save Button (only for form-based tabs) */}
                    <div className="settings-form-actions">
                        <button
                            type="submit"
                            className="admin-primary-btn save-settings-btn"
                            disabled={saving}
                        >
                            {saving ? t('saving') : t('saveSettings')}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default AdminSettings;